/**
 * Express middleware for AgentShield
 */

import type { Request, Response, NextFunction } from 'express';
import { AgentDetector } from '@kya-os/agentshield';
import type {
  ExpressMiddlewareConfig,
  AgentShieldRequest,
  AgentShieldMiddleware,
} from './types';

/**
 * Create AgentShield middleware for Express
 */
export function createAgentShieldMiddleware(
  config: Partial<ExpressMiddlewareConfig> = {}
): AgentShieldMiddleware {
  const detector = new AgentDetector(config);

  const {
    onAgentDetected = 'log',
    onDetection,
    skipPaths = [],
    blockedResponse = {
      status: 403,
      message: 'Access denied: Automated agent detected',
      headers: { 'Content-Type': 'application/json' },
    },
  } = config;

  return async (
    req: Request,
    res: Response,
    next: NextFunction
  ): Promise<void> => {
    try {
      // Check if path should be skipped
      const shouldSkip = skipPaths.some(pattern => {
        if (typeof pattern === 'string') {
          return req.path === pattern;
        }
        return pattern.test(req.path);
      });

      if (shouldSkip) {
        (req as AgentShieldRequest).agentShield = {
          result: {
            isAgent: false,
            confidence: 0,
            confidenceLevel: 'low',
            reasons: ['Path skipped'],
            timestamp: new Date(),
          },
          skipped: true,
        };
        return next();
      }

      // Prepare request context
      const context = {
        userAgent: req.get('User-Agent'),
        ipAddress: req.ip || req.connection.remoteAddress,
        headers: req.headers as Record<string, string>,
        url: req.url,
        method: req.method,
        body: req.body,
        timestamp: new Date(),
      };

      // Analyze request
      const result = await detector.analyze(context);

      // Attach result to request
      (req as AgentShieldRequest).agentShield = {
        result,
        skipped: false,
      };

      // Handle detection result
      if (
        result.isAgent &&
        result.confidence >= (config.confidenceThreshold ?? 0.7)
      ) {
        // Call custom detection handler if provided
        if (onDetection) {
          await onDetection(req, res, result);
        }

        // Handle based on configuration
        switch (onAgentDetected) {
          case 'block':
            if (blockedResponse.headers) {
              Object.entries(blockedResponse.headers).forEach(
                ([key, value]) => {
                  res.setHeader(key, value);
                }
              );
            }
            res.status(blockedResponse.status).json({
              error: blockedResponse.message,
              detected: true,
              confidence: result.confidence,
              timestamp: result.timestamp,
            });
            return;

          case 'log':
            console.warn('AgentShield: Agent detected', {
              ipAddress: context.ipAddress,
              userAgent: context.userAgent,
              confidence: result.confidence,
              reasons: result.reasons,
            });
            break;

          case 'allow':
          default:
            // Continue processing
            break;
        }
      }

      next();
    } catch (error) {
      console.error('AgentShield middleware error:', error);
      next(); // Continue on error to avoid breaking the application
    }
  };
}

/**
 * Convenience function for basic setup
 */
export function agentShield(
  config: Partial<ExpressMiddlewareConfig> = {}
): AgentShieldMiddleware {
  return createAgentShieldMiddleware(config);
}
